import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import LinkNext from 'next/link';

interface TextProps {
    $align?: 'center' | 'left' | 'right';
}

export const H1 = styled.h1<TextProps>`
    font-size: 4rem;
    font-weight: 800;
    hyphens: auto;
    line-height: 1.2em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 5.6rem;
    }

    ${media('lg')} {
        font-size: 7.2rem;
    }
`;

export const H2 = styled.h2<TextProps>`
    font-size: 2.8rem;
    font-weight: 800;
    hyphens: auto;
    line-height: 1.2em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 4rem;
    }

    ${media('lg')} {
        font-size: 4.8rem;
    }
`;

export const H3 = styled.h3<TextProps>`
    font-size: 2rem;
    font-weight: 700;
    hyphens: auto;
    line-height: 1.2em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 2.8rem;
    }

    ${media('lg')} {
        font-size: 3.2rem;
    }
`;

export const H4 = styled.h4<TextProps>`
    font-size: 1.6rem;
    font-weight: 600;
    hyphens: auto;
    line-height: 1.2em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 1.8rem;
    }

    ${media('lg')} {
        font-size: 2rem;
    }
`;

export const P = styled.p<TextProps>`
    font-size: 1.5rem;
    hyphens: auto;
    line-height: 1.6;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};

    ${media('md')} {
        font-size: 1.6rem;
    }
`;

export const Subline = styled(P)`
    align-items: center;
    background-color: ${({theme}) => theme.colors.primary};
    border-radius: 1px;
    color: ${({theme}) => theme.colors.textLight};
    display: flex;
    font-size: 2rem;
    font-weight: 700;
    gap: ${spacing(1.5)};
    justify-content: center;
    padding-block: ${spacing(0.5)};
    padding-inline: ${spacing(2)};
    width: fit-content;
`;

export const Small = styled.small`
    font-size: 1.4rem;
    hyphens: auto;
    line-height: 1.6em;
`;

export const ConsentSmall = styled.small`
    font-size: 1rem;
    hyphens: auto;
    line-height: 1.1em;
`;

export const Link = styled(LinkNext, {shouldForwardProp: prop => !prop.startsWith('$')})<TextProps>`
    color: ${({theme}) => theme.colors.primary};
    font-size: 1.6rem;
    line-height: 1.6em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-decoration: underline hsla(from ${({theme}) => theme.colors.primary} H S L / 1);
    text-underline-offset: 0.15em;
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;

    &:hover {
        text-decoration: underline hsla(from ${({theme}) => theme.colors.primary} H S L / 0);
    }

    &:focus-visible {
        color: ${({theme}) => theme.colors.primaryDark};
        outline: 1px solid ${({theme}) => theme.colors.primaryDark};
        outline-offset: 2px;
    }
`;

interface FooterLinkProps extends TextProps {
    $size?: 'medium' | 'small';
}

export const FooterLink = styled(LinkNext, {shouldForwardProp: prop => !prop.startsWith('$')})<FooterLinkProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-size: ${({$size}) => ($size === 'small' ? '1.4rem' : '1.6rem')};
    line-height: 1.6em;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 0);
    text-underline-offset: 0.15em;
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;

    &:hover {
        text-decoration: underline hsla(from ${({theme}) => theme.colors.textPrimary} H S L / 1);
    }

    &:focus-visible {
        color: ${({theme}) => theme.colors.secondaryDark};
        outline: 1px solid ${({theme}) => theme.colors.secondaryDark};
        outline-offset: 2px;
    }
`;

export const Li = styled.li`
    font-size: 1.6rem;
    line-height: 1.6;
    margin: 0;
    padding: 0;
`;

export const Ul = styled.ul`
    line-height: 1.6;
    list-style-type: disc;
    margin: 0;
    padding: 0 0 0 ${spacing(5)};

    & > ${Li} > & {
        padding: 0 0 0 ${spacing(3)};
    }

    & > ${Li} > & > ${Li}::marker {
        font-size: 1.4rem;
    }
`;

export const Ol = styled.ol`
    counter-reset: item;
    line-height: 1.6;
    margin: 0;
    padding: 0 0 0 ${spacing(5)};

    & > ${Li} {
        &::before {
            font-weight: bold;
        }
    }

    & & {
        padding: 0 0 0 ${spacing(3)};

        & > ${Li} {
            &::before {
                content: attr(value);
                font-weight: normal;
                left: -3.8rem;
                position: absolute;
            }
        }
    }

    & & & {
        padding: 0 0 0 ${spacing(1)};

        & > ${Li} {
            &::before {
                content: attr(value);
                font-weight: normal;
                left: -1.8rem;
                position: absolute;
            }
        }
    }
`;

export const BlockQuote = styled.blockquote<TextProps>`
    border-left: 4px solid ${({theme}) => theme.colors.secondary};
    font-size: 2rem;
    font-style: italic;
    font-weight: 600;
    line-height: 1.6;
    margin: 0;
    padding: 0 0 0 ${spacing(7)};
    text-align: ${({$align}) => $align ?? 'left'};
`;

export const Sup = styled.sup`
    font-size: 0.6em;
    line-height: 1;
    vertical-align: super;
`;

export const Sub = styled.sub`
    font-size: 0.6em;
    line-height: 1;
    vertical-align: sub;
`;